import { Component, createRef } from 'react'
import _ from 'lodash'
import clx from 'classnames'
import moment from 'moment'
import { Button } from '@vzmi/shp_shared_compo'
import { DEFAULT_DASHBOARD_DATE_RANGE, YESTERDAY } from '../../../../../../constants/date'
import { showOnlyNumber } from '../../../../../../lib/formatter'
import { getUIFormatDate, DATE_FORMAT } from '../../../../../../lib/MomentHelper'
import FormRow from '../../../../../Common/NewDashboard/FormRow/FormRow'
import Collapse from '../../../../../Common/NewDashboard/Collapse/Collapse'
import { IconExportButton } from '../../../../../Common/ExportButton'
import {
  RangePickerField,
  RadioGroupInputField,
  InputField
} from '../../../../../Common/Form'
import {
  formDecorator,
  Field,
  FormValueSelector
} from '../../../../../Common/forms'
import styles from './SearchForm.css'
import {
  LISTING_TYPE,
  LISTING_TYPE_OPTIONS
} from '../../../constants/listing'
import {
  CARRIER_OPTIONS,
  CARRIER
} from '../../../constants/carrier'
import {
  REFERRAL_TYPE_OPTIONS,
  NON_PVGA_REFERRAL_TYPE_OPTIONS,
  REFERRAL_TYPE
} from '../../../constants/referralType'
import DateHelper from '../../../../../../lib/DateHelper'
import { calendarDisableDate } from '../../../Shared'

export const FIELD = {
  MAIN_RANGE: 'mainRange',
  COMPARISON_RANGE: 'comparisonRange',
  LISTING_TYPE: 'listingType',
  LISTING_ID: 'listingId',
  CARRIERS: 'carriers',
  PLATFORMS: 'platforms',
  REFERRAL_TYPE: 'referralType'
}

// GA流量來源的切換日
const REFERRAL_LEGACY_CUTOFF_DATE = '2026/01/12'
// 站外-工作階段的最早查詢日期
const EXTERNAL_SESSION_MIN_DATE = '2025/11/01'

export const validateListingId = values => {
  const listingType = values[FIELD.LISTING_TYPE]
  const listingId = values[FIELD.LISTING_ID]

  if (listingType === LISTING_TYPE.ALL) {
    return {}
  }

  if (listingId === '') {
    return { [FIELD.LISTING_ID]: '請輸入賣場編號' }
  }

  return {}
}

const syncValidator = values => {
  let errors = {}
  errors = _.merge(errors, validateListingId(values))
  return errors
}

export const CONFIG = {
  form: 'PAGE_VIEWS_SOURCE_FORM',
  validate: syncValidator,
  initialValues: {
    [FIELD.MAIN_RANGE]: DEFAULT_DASHBOARD_DATE_RANGE,
    [FIELD.COMPARISON_RANGE]: [],
    [FIELD.LISTING_TYPE]: LISTING_TYPE_OPTIONS[0].value,
    [FIELD.LISTING_ID]: '',
    [FIELD.CARRIERS]: CARRIER_OPTIONS[0].value,
    [FIELD.REFERRAL_TYPE]: REFERRAL_TYPE_OPTIONS[0].value
  }
}

class SearchForm extends Component {
  constructor (props) {
    super(props)
    this.collapse = createRef()
    /*
      資料查詢區塊
      僅接受半年內之資料查詢
      起：兩年以前之日期不可選擇
      迄且現在是 12:00:00 以後：今天(含)以後之日期不可選擇
      迄且現在是 11:59:59 以前：前一天(含)以後之日期不可選擇
    */
    const {
      startMinDate
    } = DateHelper.getSaleStatDefaultPeriod()
    const [start, end] = DEFAULT_DASHBOARD_DATE_RANGE
    const formatStartDate = getUIFormatDate(start)
    const formatEndDate = getUIFormatDate(end)
    this.state = {
      periodStartMinDate: startMinDate,
      periodEndMaxDate: YESTERDAY,
      periodStart: formatStartDate,
      secondPeriodStart: formatStartDate,
      periodEnd: formatEndDate,
      secondPeriodEnd: formatEndDate
    }
  }

  componentDidMount () {
    // FIXME: isPVGA 開關拔掉時，整段可回復為直接執行 onFormInit(this.props) 降低複雜度
    const { isPVGA, change, onFormInit } = this.props
    // 根據 isPVGA 動態設置 referralType 初始值
    // CONFIG.initialValues 中預設是 REFERRAL_TYPE_OPTIONS[0].value（適用於 isPVGA 為 true）
    // 如果 isPVGA 為 false，需要使用 change 更新為 NON_PVGA_REFERRAL_TYPE_OPTIONS[0].value
    if (change && !isPVGA) {
      change(FIELD.REFERRAL_TYPE, NON_PVGA_REFERRAL_TYPE_OPTIONS[0].value)
    }
    // 使用 setTimeout 確保 Redux 狀態更新完成後再調用 onFormInit
    // 這樣可以確保 handleSubmit 時使用的是正確的初始值
    setTimeout(() => {
      onFormInit(this.props)
    }, 0)
  }

  // 調整「起」的日期後，則「迄」跟著自動變動：
  // 「起」＋六個月 (180 天) ; 之後的日期 disable 不可選
  // 若「起」＋六個月 (180 天)  > 昨日，則為昨日
  disabledDate = dateMoment => {
    const {
      periodEndMaxDate,
      periodStartMinDate,
      periodStart
    } = this.state
    return dateMoment < moment(periodStartMinDate) ||
    dateMoment >= calendarDisableDate(periodEndMaxDate, periodStart)
  }

  onCalendarChange = dateMoment => {
    const periodStart = getUIFormatDate(dateMoment[0])
    this.setState({
      periodStart
    })
  }

  // 檢查並調整日期區間（處理所有流量來源的情境）
  adjustMainRangeIfNeeded = (dateMoment, dateString, referralType) => {
    let adjustedDateMoment = dateMoment
    let adjustedDateString = dateString

    // 如果沒有選擇完整的日期範圍，直接返回
    if (!dateMoment || !dateMoment[0] || !dateMoment[1]) {
      return { dateMoment, dateString }
    }

    // 處理「站內-瀏覽量」和「站外-瀏覽量」：結束日期不能晚於 2026/01/12
    if (referralType === REFERRAL_TYPE.INTERNAL || referralType === REFERRAL_TYPE.EXTERNAL) {
      const cutoffMoment = moment(REFERRAL_LEGACY_CUTOFF_DATE, DATE_FORMAT.UI)
      const endMoment = moment(adjustedDateMoment[1])

      // 如果結束日期晚於截止日期，則調整
      if (endMoment.isAfter(cutoffMoment)) {
        adjustedDateMoment = [adjustedDateMoment[0], cutoffMoment]
        adjustedDateString = [
          adjustedDateString[0],
          getUIFormatDate(cutoffMoment)
        ]
      }
    }

    // 處理「站外-工作階段」：開始日期不能早於 2025/11/01
    if (referralType === REFERRAL_TYPE.EXTERNAL_SESSION) {
      const minDateMoment = moment(EXTERNAL_SESSION_MIN_DATE, DATE_FORMAT.UI)
      const startMoment = moment(adjustedDateMoment[0])

      // 如果開始日期早於最早日期，則調整
      if (startMoment.isBefore(minDateMoment)) {
        adjustedDateMoment = [minDateMoment, adjustedDateMoment[1]]
        adjustedDateString = [
          getUIFormatDate(minDateMoment),
          adjustedDateString[1]
        ]
      }
    }

    return {
      dateMoment: adjustedDateMoment,
      dateString: adjustedDateString
    }
  }

  onMainRangeChange = (dateMoment, dateString) => {
    const referralType = _.get(this.props, 'reduxFormState.values.referralType')

    // 檢查並調整日期區間（處理所有流量來源的情境）
    const adjusted = this.adjustMainRangeIfNeeded(dateMoment, dateString, referralType)
    const [periodStart, periodEnd] = adjusted.dateString

    this.setState({
      periodStart,
      periodEnd
    })
  }

  secondDisabledDate = dateMoment => {
    const {
      periodEndMaxDate,
      periodStartMinDate,
      secondPeriodStart
    } = this.state
    return dateMoment < moment(periodStartMinDate) ||
    dateMoment >= calendarDisableDate(periodEndMaxDate, secondPeriodStart)
  }

  secondOnCalendarChange = dateMoment => {
    const secondPeriodStart = getUIFormatDate(dateMoment[0])
    this.setState({
      secondPeriodStart
    })
  }

  onSecondMainRangeChange = (dateMoment, dateString) => {
    const [secondPeriodStart, secondPeriodEnd] = dateString
    this.setState({
      secondPeriodStart,
      secondPeriodEnd
    })
  }

  onListingChange = (e, value) => {
    this.props.untouch(FIELD.LISTING_ID)
    this.props.onListingChange(e, value)
  }

  onReferralTypeChange = (e, value) => {
    const { onReferralTypeChange: parentOnReferralTypeChange, change, untouch } = this.props

    // 當選擇「站外-工作階段」時，重置賣場為全部並清空賣場編號、且裝置為全部
    if (value === REFERRAL_TYPE.EXTERNAL_SESSION) {
      change(FIELD.LISTING_TYPE, LISTING_TYPE.ALL)
      change(FIELD.LISTING_ID, '')
      untouch(FIELD.LISTING_ID)
      change(FIELD.CARRIERS, CARRIER.ALL)
    }

    // 當切換流量來源時，檢查並調整日期區間（處理所有流量來源的情境）
    const mainRange = _.get(this.props, 'reduxFormState.values.mainRange', [])
    if (mainRange && mainRange.length === 2) {
      // mainRange 在 Redux Form 中存的是 moment 物件陣列
      const dateMoment = mainRange.map(d => moment.isMoment(d) ? d : moment(d))
      const dateString = mainRange.map(d => {
        const m = moment.isMoment(d) ? d : moment(d)
        return getUIFormatDate(m)
      })
      this.adjustMainRangeIfNeeded(dateMoment, dateString, value)
    }

    // 調用父組件的 onReferralTypeChange
    if (parentOnReferralTypeChange) {
      parentOnReferralTypeChange(e, value)
    }
  }

  onSubmit = (e) => {
    this.props.handleSubmit(e)
    const comparisonRange = _.get(this.props, 'reduxFormState.values.comparisonRange', [])
    // 打開比較日期但沒有輸入在查詢時會自動關閉
    if (_.isEmpty(comparisonRange)) {
      this.collapse.current.open(false)
    }
  }

  render () {
    const {
      onCollapseChange,
      onCarriersChange,
      onExportExcel,
      disableExportBtn,
      isPVGA
    } = this.props
    return (
      <form
        onSubmit={this.onSubmit}
        className={styles.searchForm}
      >
        <FormRow
          label='日期區間'
          labelAlign='center'
        >
          <Field
            key={FIELD.MAIN_RANGE}
            name={FIELD.MAIN_RANGE}
            data-role={FIELD.MAIN_RANGE}
            className={styles.rangePicker}
            component={RangePickerField}
            onChange={this.onMainRangeChange}
            disabledDate={this.disabledDate}
            onCalendarChange={this.onCalendarChange}
            allowClear={false}
          />
        </FormRow>
        <FormRow className={styles.comparisonRangePickerRow}>
          <Collapse
            ref={this.collapse}
            id='comparison-collapse'
            openText='比較日期'
            closeText='取消比較'
            tipsText='可查詢最早日期為兩年前，起迄區間最長為六個月，每日中午更新'
            className={styles.rangePicker}
            actionClassName={styles.actionClassName}
            onChange={onCollapseChange}
          >
            <Field
              key={FIELD.COMPARISON_RANGE}
              name={FIELD.COMPARISON_RANGE}
              data-role={FIELD.COMPARISON_RANGE}
              className={clx(styles.rangePicker, styles.comparisonRangePicker)}
              component={RangePickerField}
              onChange={this.onSecondMainRangeChange}
              disabledDate={this.secondDisabledDate}
              onCalendarChange={this.secondOnCalendarChange}
            />
          </Collapse>
        </FormRow>
        <FormValueSelector fieldName={FIELD.REFERRAL_TYPE}>
          {referralType => {
            const isExternalSession = referralType === REFERRAL_TYPE.EXTERNAL_SESSION
            // 當選擇「站外-工作階段」時，disable 指定賣場選項
            const listingTypeOptions = isExternalSession
              ? LISTING_TYPE_OPTIONS.map(option => ({
                ...option,
                disabled: option.value === LISTING_TYPE.ORDER
              }))
              : LISTING_TYPE_OPTIONS

            return (
              <>
                <FormRow
                  label='賣場'
                  labelAlign='top'
                >
                  <Field
                    key={FIELD.LISTING_TYPE}
                    name={FIELD.LISTING_TYPE}
                    data-role={FIELD.LISTING_TYPE}
                    className={styles.listingType}
                    component={RadioGroupInputField}
                    buttonStyle='outline'
                    options={listingTypeOptions}
                    onChange={this.onListingChange}
                  />
                </FormRow>
                <FormValueSelector fieldName={FIELD.LISTING_TYPE}>
                  {listingType => {
                    const isFieldVisible = listingType === LISTING_TYPE.ORDER && !isExternalSession
                    return (
                      <FormRow
                        className={clx(
                          styles.listingIdRow,
                          { hidden: !isFieldVisible }
                        )}
                      >
                        <Field
                          key={FIELD.LISTING_ID}
                          name={FIELD.LISTING_ID}
                          data-role={FIELD.LISTING_ID}
                          className={styles.listingId}
                          component={InputField}
                          placeholder='輸入賣場編號'
                          parse={showOnlyNumber}
                          disabled={isExternalSession}
                        />
                      </FormRow>
                    )
                  }}
                </FormValueSelector>
                <FormRow
                  label='裝置'
                  labelAlign='top'
                >
                  <Field
                    key={FIELD.CARRIERS}
                    name={FIELD.CARRIERS}
                    data-role={FIELD.CARRIERS}
                    component={RadioGroupInputField}
                    buttonStyle='outline'
                    options={CARRIER_OPTIONS.map(option => ({
                      ...option,
                      disabled: isExternalSession && option.value !== CARRIER.ALL
                    }))}
                    onChange={onCarriersChange}
                  />
                </FormRow>
              </>
            )
          }}
        </FormValueSelector>
        <FormRow
          label='流量來源'
          labelAlign='top'
        >
          <Field
            key={FIELD.REFERRAL_TYPE}
            name={FIELD.REFERRAL_TYPE}
            data-role={FIELD.REFERRAL_TYPE}
            component={RadioGroupInputField}
            buttonStyle='outline'
            options={isPVGA ? REFERRAL_TYPE_OPTIONS : NON_PVGA_REFERRAL_TYPE_OPTIONS}
            onChange={this.onReferralTypeChange}
          />
        </FormRow>
        <FormRow labelAlign='top'>
          <Button
            type='submit'
            data-role='search-btn'
            size='large'
            className={styles.searchBtn}
          >
            查詢
          </Button>
          <div className={styles.exportButton}>
            <IconExportButton
              disabled={disableExportBtn}
              data-role='pv-daily-export-button'
              onClick={onExportExcel}
              label='下載整筆檔案'
            />
          </div>
        </FormRow>
      </form>
    )
  }
}

const SearchFormWithDecorator = formDecorator(CONFIG)(SearchForm)

export default SearchFormWithDecorator
